import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import type { BillingOverview } from "../../shared/billing-schemas";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent } from "../components/ui/dialog";
import {
  splitAgreements,
  termsOf,
  unifyAgreements,
  type Agreement,
  type AgreementActionKind,
} from "./billing-agreements";
import {
  createApprovedBillingTerms,
  createBillingQuote,
  createTermsFromDocument,
  getSignedDocuments,
  signedDocumentsKey,
  transitionBillingQuote,
  transitionBillingTerms,
  updateBillingQuote,
  updateBillingTerms,
} from "./billing-api";
import type { QuoteFormTarget, QuoteSubmission } from "./billing-quote-draft";
import {
  BillingAgreementActionDialog,
  type AgreementAction,
  type AgreementActionInput,
} from "./BillingAgreementActionDialog";
import { BillingAgreementCard } from "./BillingAgreementCard";
import { offeredKinds } from "./billing-collect-request";
import { BillingCollectSheet, type CollectTarget } from "./BillingCollectForm";
import { documentSource } from "./billing-document-terms";
import { DealBilling } from "./BillingDealBilling";
import { BillingSignedDocuments } from "./BillingSignedDocuments";
import { BillingQuoteForm } from "./BillingQuoteForm";
import { SubscriptionSheet } from "./BillingRecords";
import { EmptyCard, PageSection } from "./PageSection";

/**
 * What the customer pays for (ADR-0077, ADR-0087): everything offered to or agreed with them, one
 * card per deal, each showing how its terms are billed and the next thing to do. What still needs
 * a decision comes first; what is only history waits under a toggle.
 */

type Act = (item: Agreement, kind: AgreementActionKind) => void;

function editorKey(target: QuoteFormTarget): string {
  if (target.mode === "create") return "create";
  if (target.mode === "document") return `document-${target.document.documentId}`;
  if (target.mode === "edit" || target.mode === "replace") {
    return `${target.mode}-${target.quote.id}`;
  }
  return `${target.mode}-${target.terms.id}`;
}

async function save(customerSlug: string, submission: QuoteSubmission): Promise<unknown> {
  switch (submission.kind) {
    case "quote":
      return createBillingQuote(customerSlug, submission.input);
    case "quote_update":
      return updateBillingQuote(customerSlug, submission.quoteId, submission.input);
    case "approved_terms":
      return createApprovedBillingTerms(customerSlug, submission.input);
    case "document_terms":
      return createTermsFromDocument(customerSlug, {
        documentId: submission.documentId,
        terms: submission.input,
      });
    case "terms_update":
      return updateBillingTerms(customerSlug, submission.termsId, submission.input);
  }
}

async function transition(
  customerSlug: string,
  action: AgreementAction,
  input: AgreementActionInput,
): Promise<unknown> {
  if (action.kind === "quote") {
    return transitionBillingQuote(customerSlug, action.quoteId, action.action, {
      reason: input.reason,
      validUntil: input.validUntil,
    });
  }
  return transitionBillingTerms(customerSlug, action.termsId, action.action, {
    reason: input.reason,
    endsAt: input.endsAt,
  });
}

function useAgreementActions(customerSlug: string, refresh: () => Promise<void>) {
  const [editor, setEditor] = useState<QuoteFormTarget | null>(null);
  const [action, setAction] = useState<AgreementAction | null>(null);
  const saving = useMutation({
    mutationFn: (submission: QuoteSubmission) => save(customerSlug, submission),
    onSuccess: async () => {
      setEditor(null);
      await refresh();
    },
  });
  const acting = useMutation({
    mutationFn: (input: { action: AgreementAction } & AgreementActionInput) =>
      transition(customerSlug, input.action, input),
    onSuccess: async () => {
      setAction(null);
      await refresh();
    },
  });
  const openEditor = (target: QuoteFormTarget | null) => {
    saving.reset();
    setEditor(target);
  };
  const openAction = (next: AgreementAction | null) => {
    acting.reset();
    setAction(next);
  };
  return { editor, openEditor, action, openAction, saving, acting };
}

/** The form an action button opens, if it opens one. */
function editorFor(item: Agreement, kind: AgreementActionKind): QuoteFormTarget | null {
  const terms = termsOf(item);
  if (kind === "edit" && item.kind === "quote") return { mode: "edit", quote: item.quote };
  if (kind === "edit_terms" && terms) return { mode: "edit_terms", terms };
  if (kind !== "replace") return null;
  if (item.status === "active" && terms) return { mode: "revise", terms };
  return item.kind === "quote" ? { mode: "replace", quote: item.quote } : null;
}

/** The step an action button confirms, if it confirms one. */
function actionFor(item: Agreement, kind: AgreementActionKind): AgreementAction | null {
  if (kind === "send" || kind === "cancel_quote") {
    return { kind: "quote", quoteId: item.id, action: kind === "send" ? "send" : "cancel" };
  }
  const terms = termsOf(item);
  if (terms === null) return null;
  if (kind === "approve" || kind === "end")
    return { kind: "terms", termsId: terms.id, action: kind };
  return kind === "cancel_terms" ? { kind: "terms", termsId: terms.id, action: "cancel" } : null;
}

function QuoteFormDialog({
  editor,
  billing,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  editor: QuoteFormTarget | null;
  billing: BillingOverview;
  pending: boolean;
  error: string | null;
  onSubmit: (submission: QuoteSubmission) => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={editor !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-3xl">
        {editor ? (
          <>
            <BillingQuoteForm
              key={editorKey(editor)}
              target={editor}
              tools={billing.tools}
              versions={billing.terms}
              pending={pending}
              error={error}
              onSubmit={onSubmit}
              onCancel={onClose}
            />
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function AgreementList({
  customerSlug,
  items,
  label,
  defaultOpen,
  canManage,
  onAct,
  billingOf,
  signedOf,
}: {
  customerSlug: string;
  items: Agreement[];
  label: string;
  defaultOpen: boolean;
  canManage: boolean;
  onAct: Act;
  billingOf?: (item: Agreement) => ReactNode;
  signedOf?: (item: Agreement) => boolean;
}) {
  return (
    <ul className="flex flex-col gap-3" aria-label={label}>
      {items.map((item) => (
        <BillingAgreementCard
          key={item.id}
          customerSlug={customerSlug}
          item={item}
          canManage={canManage}
          defaultOpen={defaultOpen}
          onAct={onAct}
          billing={billingOf?.(item)}
          signed={signedOf?.(item) ?? false}
        />
      ))}
    </ul>
  );
}

/** History, folded away until asked for: superseded, ended, declined, expired, canceled. */
function PastQuotes({
  customerSlug,
  items,
  canManage,
  onAct,
}: {
  customerSlug: string;
  items: Agreement[];
  canManage: boolean;
  onAct: Act;
}) {
  const [shown, setShown] = useState(false);
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col items-start gap-3">
      <Button
        type="button"
        variant="ghost"
        aria-expanded={shown}
        onClick={() => {
          setShown((value) => !value);
        }}
      >
        {shown ? "Hide past quotes" : `Past quotes (${items.length})`}
      </Button>
      {shown ? (
        <AgreementList
          customerSlug={customerSlug}
          items={items}
          label="Past quotes"
          defaultOpen={false}
          canManage={canManage}
          onAct={onAct}
        />
      ) : null}
    </div>
  );
}

type SectionProps = {
  customerSlug: string;
  billing: BillingOverview;
  canManage: boolean;
  canCollect: boolean;
  refresh: () => Promise<void>;
};

/**
 * How each card's approved terms are billed, and the two sheets its buttons open: the collect
 * sheet set to those terms, and a subscription's own sheet.
 */
function useDealBilling({ customerSlug, billing, canManage, canCollect, refresh }: SectionProps) {
  const [target, setTarget] = useState<CollectTarget | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const kinds = offeredKinds(billing, canManage, canCollect);
  const signed = useQuery({
    queryKey: signedDocumentsKey(customerSlug),
    queryFn: () => getSignedDocuments(customerSlug),
  });
  const sourceOf = (termsId: string) =>
    signed.data?.documents.find((document) => document.termsId === termsId) ?? null;
  const subscription =
    billing.subscriptions.find((candidate) => candidate.id === subscriptionId) ?? null;
  const billingOf = (item: Agreement) => {
    const terms = termsOf(item);
    if (terms?.status !== "approved") return null;
    return (
      <DealBilling
        terms={terms}
        source={sourceOf(terms.id)}
        customerSlug={customerSlug}
        billing={billing}
        kinds={kinds}
        onCollect={(kind) => {
          setTarget({ termsId: terms.id, kind });
        }}
        onOpenSubscription={(chosen) => {
          setSubscriptionId(chosen.id);
        }}
      />
    );
  };
  const sheets = (
    <>
      <BillingCollectSheet
        customerSlug={customerSlug}
        billing={billing}
        canManage={canManage}
        canCollect={canCollect}
        target={target}
        onClose={() => {
          setTarget(null);
        }}
        onChanged={refresh}
      />
      <SubscriptionSheet
        customerSlug={customerSlug}
        subscription={subscription}
        canManage={canManage}
        canCollect={canCollect}
        onChanged={refresh}
        onClose={() => {
          setSubscriptionId(null);
        }}
      />
    </>
  );
  // Terms set up from what the customer signed are theirs, not approved internally.
  const signedOf = (item: Agreement) => {
    const terms = termsOf(item);
    return terms !== null && sourceOf(terms.id) !== null;
  };
  return { billingOf, signedOf, sheets };
}

/** The two dialogs the cards open: the quote and terms form, and a quote or terms step. */
function AgreementDialogs({
  actions,
  billing,
}: {
  actions: ReturnType<typeof useAgreementActions>;
  billing: BillingOverview;
}) {
  return (
    <>
      <QuoteFormDialog
        editor={actions.editor}
        billing={billing}
        pending={actions.saving.isPending}
        error={actions.saving.error?.message ?? null}
        onSubmit={(submission) => {
          actions.saving.mutate(submission);
        }}
        onClose={() => {
          actions.openEditor(null);
        }}
      />
      <BillingAgreementActionDialog
        action={actions.action}
        pending={actions.acting.isPending}
        error={actions.acting.error?.message ?? null}
        onSubmit={(input) => {
          if (actions.action) actions.acting.mutate({ action: actions.action, ...input });
        }}
        onClose={() => {
          actions.openAction(null);
        }}
      />
    </>
  );
}

export function BillingAgreements(props: SectionProps) {
  const { customerSlug, billing, canManage, refresh } = props;
  const deals = useDealBilling(props);
  const actions = useAgreementActions(customerSlug, refresh);
  const { current, past } = splitAgreements(unifyAgreements(billing));
  const act: Act = (item, kind) => {
    const editor = editorFor(item, kind);
    if (editor) {
      actions.openEditor(editor);
      return;
    }
    const next = actionFor(item, kind);
    if (next) actions.openAction(next);
  };
  return (
    <PageSection
      title="What they pay for"
      id="billing-quotes-title"
      level="h3"
      description="Quotes you send, the terms they accept, and how each is billed."
      action={
        canManage ? (
          <Button
            type="button"
            onClick={() => {
              actions.openEditor({ mode: "create" });
            }}
          >
            New quote
          </Button>
        ) : null
      }
    >
      <BillingSignedDocuments
        customerSlug={customerSlug}
        canManage={canManage}
        onReview={(document) => {
          const source = documentSource(document, billing.tools);
          actions.openEditor({ mode: "document", document, source });
        }}
        onAdded={refresh}
      />
      {current.length === 0 && past.length === 0 ? (
        <EmptyCard>No quotes have been drafted for this customer.</EmptyCard>
      ) : null}
      {current.length > 0 ? (
        <AgreementList
          customerSlug={customerSlug}
          items={current}
          label="Quotes"
          defaultOpen={false}
          canManage={canManage}
          onAct={act}
          billingOf={deals.billingOf}
          signedOf={deals.signedOf}
        />
      ) : null}
      <PastQuotes customerSlug={customerSlug} items={past} canManage={canManage} onAct={act} />
      {deals.sheets}
      <AgreementDialogs actions={actions} billing={billing} />
    </PageSection>
  );
}
